import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import { api, openCourseFile } from '../../lib/api'
import {
  ArrowLeft,
  BookOpen,
  FileText,
  Bell,
  Folder,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ChevronDown,
  ChevronRight,
  Download,
  ScrollText,
  GraduationCap,
  Scale,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { useState } from 'react'
import SpellingProgress from '../../components/parent/SpellingProgress'
import { CourseGradeChart } from '../../components/parent/CourseGradeChart'

// Types
interface ModuleItem {
  id: number
  title: string
  type: string
  completed: boolean
  indent: number
}

interface Module {
  id: number
  name: string
  position: number
  state: string
  items_count: number
  items_completed: number
  progress_pct: number
  items: ModuleItem[]
}

interface Announcement {
  id: number
  title: string
  message: string
  author: string
  posted_at: string | null
  read: boolean
  read_at: string | null
}

interface CourseFile {
  id: number
  name: string
  size: number | null
  lesson_start: number | null
  lesson_end: number | null
  url: string
  has_local: boolean
}

interface Assignment {
  id: number
  name: string
  due_at: string | null
  score: number | null
  grade: string | null
  points_possible: number | null
  status: string
  missing: boolean
  late: boolean
  excused: boolean
}

interface WikiPage {
  url: string
  title: string
  created_at: string | null
  updated_at: string | null
}

interface GradeGroup {
  name: string
  weight: number
  earned_points: number
  total_points: number
  average: number | null
  contribution: number | null
  graded_count: number
  pending_count: number
  assignments: Array<{
    id: number
    name: string
    points_possible: number
    due_at: string | null
    score: number | null
    grade: string | null
    status: string
  }>
}

interface CanvasGradesData {
  course: {
    id: number
    canvas_id: number
    name: string
    current_score: number | null
  }
  weighting: {
    total_weight: number
    is_weighted: boolean
    calculated_grade: number | null
  }
  groups: GradeGroup[]
}

interface CourseDetailData {
  course: {
    id: number
    name: string
    term: string | null
    current_score: number | null
    current_grade: string | null
    missing_count: number
    grade_updated_at: string | null
    syllabus_body: string | null
  }
  student: {
    id: number
    name: string
  }
  capabilities: {
    has_modules: boolean
    has_announcements: boolean
    has_files: boolean
    has_pages: boolean
    has_weighted_grades: boolean
    grading_scheme: string | null
  } | null
  modules: Module[]
  announcements: {
    items: Announcement[]
    unread_count: number
  }
  files: Record<string, CourseFile[]>
  pages: WikiPage[]
  assignments: Assignment[]
  skill_insights: Array<{
    skill_id: number
    mastery_level: number
    trend: string | null
  }>
  grade_history: Array<{
    date: string | null
    score: number | null
    grade: string | null
  }>
}

// Module Progress Card Component
function ModuleProgressCard({ modules }: { modules: Module[] }) {
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set())

  const toggleModule = (moduleId: number) => {
    setExpandedModules((prev) => {
      const next = new Set(prev)
      if (next.has(moduleId)) {
        next.delete(moduleId)
      } else {
        next.add(moduleId)
      }
      return next
    })
  }

  if (modules.length === 0) {
    return null
  }

  return (
    <div className="card">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-quest-primary" />
        Module Progress
      </h2>
      <div className="space-y-2">
        {modules.map((module) => {
          const isExpanded = expandedModules.has(module.id)
          return (
            <div key={module.id} className="bg-surface-card/50 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleModule(module.id)}
                className="w-full p-3 flex items-center gap-3 hover:bg-surface-hover/50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium truncate">{module.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          module.progress_pct >= 100
                            ? 'bg-green-500'
                            : module.progress_pct > 0
                              ? 'bg-quest-primary'
                              : 'bg-gray-600'
                        }`}
                        style={{ width: `${module.progress_pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {module.items_completed}/{module.items_count}
                    </span>
                  </div>
                </div>
                <span
                  className={`text-sm font-medium ${
                    module.progress_pct >= 100
                      ? 'text-green-400'
                      : module.progress_pct > 0
                        ? 'text-quest-primary'
                        : 'text-gray-500'
                  }`}
                >
                  {Math.round(module.progress_pct)}%
                </span>
              </button>
              {isExpanded && module.items.length > 0 && (
                <div className="px-3 pb-3 space-y-1">
                  {module.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 py-1.5 text-sm"
                      style={{ paddingLeft: `${(item.indent || 0) * 16 + 8}px` }}
                    >
                      {item.completed ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border border-gray-500 flex-shrink-0" />
                      )}
                      <span
                        className={`truncate ${item.completed ? 'text-gray-400' : 'text-gray-200'}`}
                      >
                        {item.title}
                      </span>
                      <span className="text-xs text-gray-600 flex-shrink-0">{item.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Announcements Card Component
function AnnouncementsCard({
  announcements,
  onMarkRead,
}: {
  announcements: { items: Announcement[]; unread_count: number }
  onMarkRead: (id: number) => void
}) {
  const [expandedAnnouncement, setExpandedAnnouncement] = useState<number | null>(null)

  if (announcements.items.length === 0) {
    return null
  }

  return (
    <div className="card">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Bell className="w-5 h-5 text-yellow-400" />
        Announcements
        {announcements.unread_count > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
            {announcements.unread_count} new
          </span>
        )}
      </h2>
      <div className="space-y-2">
        {announcements.items.map((announcement) => (
          <div
            key={announcement.id}
            className={`p-3 rounded-lg transition-colors ${
              announcement.read ? 'bg-surface-card/30' : 'bg-yellow-500/10 border border-yellow-500/30'
            }`}
          >
            <div
              className="flex items-start gap-2 cursor-pointer"
              onClick={() => {
                setExpandedAnnouncement(
                  expandedAnnouncement === announcement.id ? null : announcement.id
                )
                if (!announcement.read) {
                  onMarkRead(announcement.id)
                }
              }}
            >
              {!announcement.read && (
                <span className="w-2 h-2 rounded-full bg-yellow-400 mt-2 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${announcement.read ? 'text-gray-300' : 'text-white'}`}>
                  {announcement.title}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {announcement.author && `${announcement.author} • `}
                  {announcement.posted_at &&
                    formatDistanceToNow(new Date(announcement.posted_at), { addSuffix: true })}
                </p>
              </div>
              {expandedAnnouncement === announcement.id ? (
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
            </div>
            {expandedAnnouncement === announcement.id && announcement.message && (
              <div
                className="mt-3 pt-3 border-t border-gray-700/50 text-sm text-gray-300 prose prose-invert prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(announcement.message) }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Course Files Grid Component
function CourseFilesGrid({ files }: { files: Record<string, CourseFile[]> }) {
  const [activeCategory, setActiveCategory] = useState<string>('all')

  const categories = Object.keys(files).filter((cat) => files[cat].length > 0)
  const allFiles = Object.values(files).flat()

  if (allFiles.length === 0) {
    return null
  }

  const displayFiles = activeCategory === 'all' ? allFiles : files[activeCategory] || []

  const categoryLabels: Record<string, string> = {
    textbook: 'Textbooks',
    homework: 'Homework',
    worksheet: 'Worksheets',
    classwork: 'Classwork',
    test: 'Tests',
    other: 'Other',
  }

  return (
    <div className="card">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <Folder className="w-5 h-5 text-blue-400" />
        Course Files
      </h2>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
            activeCategory === 'all'
              ? 'bg-quest-primary text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          All ({allFiles.length})
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-3 py-1 rounded-lg text-sm transition-colors ${
              activeCategory === cat
                ? 'bg-quest-primary text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {categoryLabels[cat] || cat} ({files[cat].length})
          </button>
        ))}
      </div>

      {/* Files Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {displayFiles.slice(0, 12).map((file) => (
          <button
            key={file.id}
            onClick={() => openCourseFile(file.id)}
            className="p-3 bg-surface-card/50 rounded-lg hover:bg-surface-hover/50 transition-colors group text-left"
          >
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-6 h-6 text-blue-400 flex-shrink-0" />
              {file.has_local ? (
                <Download className="w-4 h-4 text-green-400 flex-shrink-0" />
              ) : (
                <Download className="w-4 h-4 text-gray-500 flex-shrink-0 opacity-0 group-hover:opacity-100" />
              )}
            </div>
            <p className="text-sm font-medium truncate" title={file.name}>
              {file.name}
            </p>
            {file.lesson_start && (
              <p className="text-xs text-gray-500 mt-1">
                L{file.lesson_start}
                {file.lesson_end && file.lesson_end !== file.lesson_start && `-${file.lesson_end}`}
              </p>
            )}
          </button>
        ))}
      </div>
      {displayFiles.length > 12 && (
        <p className="text-center text-sm text-gray-500 mt-3">
          +{displayFiles.length - 12} more files
        </p>
      )}
    </div>
  )
}

// Wiki Pages Card Component
function WikiPagesCard({
  pages,
  courseId,
}: {
  pages: WikiPage[]
  courseId: number
}) {
  const [expandedPage, setExpandedPage] = useState<string | null>(null)
  const [pageContent, setPageContent] = useState<Record<string, string>>({})
  const [loadingPage, setLoadingPage] = useState<string | null>(null)

  if (pages.length === 0) {
    return null
  }

  const handlePageClick = async (pageUrl: string) => {
    if (expandedPage === pageUrl) {
      setExpandedPage(null)
      return
    }

    setExpandedPage(pageUrl)

    // Fetch content if not already loaded
    if (!pageContent[pageUrl]) {
      setLoadingPage(pageUrl)
      try {
        const response = await api.get(`/api/parent/courses/${courseId}/pages/${pageUrl}`)
        setPageContent((prev) => ({ ...prev, [pageUrl]: response.data.body || '' }))
      } catch (error) {
        console.error('Failed to load page content:', error)
        setPageContent((prev) => ({ ...prev, [pageUrl]: '<p class="text-red-400">Failed to load page content</p>' }))
      } finally {
        setLoadingPage(null)
      }
    }
  }

  return (
    <div className="card">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <ScrollText className="w-5 h-5 text-purple-400" />
        Wiki Pages
        <span className="ml-2 text-sm font-normal text-gray-400">({pages.length})</span>
      </h2>
      <div className="space-y-2">
        {pages.map((page) => (
          <div
            key={page.url}
            className="bg-surface-card/50 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => handlePageClick(page.url)}
              className="w-full p-3 flex items-center gap-3 hover:bg-surface-hover/50 transition-colors text-left"
            >
              {expandedPage === page.url ? (
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{page.title}</p>
                {page.updated_at && (
                  <p className="text-xs text-gray-500">
                    Updated {formatDistanceToNow(new Date(page.updated_at), { addSuffix: true })}
                  </p>
                )}
              </div>
            </button>
            {expandedPage === page.url && (
              <div className="px-3 pb-3">
                {loadingPage === page.url ? (
                  <div className="animate-pulse h-20 bg-gray-700 rounded"></div>
                ) : (
                  <div
                    className="p-3 bg-surface-base/50 rounded text-sm prose prose-invert prose-sm max-w-none overflow-auto max-h-96"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pageContent[page.url] || '') }}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Syllabus Card Component
function SyllabusCard({ syllabus }: { syllabus: string | null }) {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!syllabus) {
    return null
  }

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 text-left"
      >
        <GraduationCap className="w-5 h-5 text-emerald-400" />
        <h2 className="text-lg font-bold flex-1">Course Syllabus</h2>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div
          className="mt-4 p-4 bg-surface-card/50 rounded-lg prose prose-invert prose-sm max-w-none overflow-auto max-h-96"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(syllabus) }}
        />
      )}
    </div>
  )
}

// Grade Weights Card Component
function GradeWeightsCard({ courseId }: { courseId: number }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<CanvasGradesData>({
    queryKey: ['canvas-grades', courseId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/courses/${courseId}/canvas-grades`)
      return response.data
    },
    enabled: isExpanded,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const getGradeColor = (average: number | null) => {
    if (average === null) return 'text-gray-400'
    if (average >= 90) return 'text-green-400'
    if (average >= 80) return 'text-blue-400'
    if (average >= 70) return 'text-yellow-400'
    return 'text-red-400'
  }

  const normalizeGroupName = (name: string) => {
    const clean = name.trim()
    const upper = clean.toUpperCase()

    if (upper === 'FA' || upper.includes('FACT ASSESSMENT')) return 'Fact Assessments'
    if (upper === 'WA' || upper.includes('WRITTEN ASSESSMENT')) return 'Written Assessments'
    if (upper.includes('HOMEWORK') || upper === 'HW') return 'Homework'

    return clean
  }

  const getGroupPriority = (name: string) => {
    const normalized = normalizeGroupName(name)
    if (normalized === 'Homework') return 0
    if (normalized === 'Fact Assessments') return 1
    if (normalized === 'Written Assessments') return 2
    return 10
  }

  const displayGroups = (data?.groups ?? []).slice().sort((a, b) => {
    const ap = getGroupPriority(a.name)
    const bp = getGroupPriority(b.name)
    if (ap !== bp) return ap - bp
    return a.name.localeCompare(b.name)
  })

  return (
    <div className="card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 text-left"
      >
        <Scale className="w-5 h-5 text-indigo-400" />
        <h2 className="text-lg font-bold flex-1">Grade Breakdown</h2>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-4">
          {isLoading && (
            <div className="animate-pulse space-y-3">
              <div className="h-16 bg-surface-card rounded-lg"></div>
              <div className="h-16 bg-surface-card rounded-lg"></div>
            </div>
          )}

          {error && (
            <p className="text-sm text-gray-400">Unable to load grade weights</p>
          )}

          {data && (
            <div className="space-y-3">
              {/* Summary */}
              {data.weighting.is_weighted && data.weighting.calculated_grade !== null && (
                <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Weighted Grade</span>
                    <span className={`text-lg font-bold ${getGradeColor(data.weighting.calculated_grade)}`}>
                      {data.weighting.calculated_grade.toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Groups */}
              {displayGroups.map((group, idx) => {
                const groupKey = `${group.name}-${idx}`
                const displayName = normalizeGroupName(group.name)
                return (
                <div key={groupKey} className="bg-surface-card/50 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedGroup(expandedGroup === groupKey ? null : groupKey)}
                    className="w-full p-3 flex items-center gap-3 hover:bg-surface-hover/50 transition-colors"
                  >
                    {expandedGroup === groupKey ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{displayName}</p>
                        <span className="px-2 py-0.5 text-xs rounded bg-indigo-500/20 text-indigo-300">
                          {group.weight}%
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm">
                        <span className="text-gray-400">
                          {group.graded_count} graded
                          {group.pending_count > 0 && `, ${group.pending_count} pending`}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-bold ${getGradeColor(group.average)}`}>
                        {group.average !== null ? `${group.average.toFixed(1)}%` : '—'}
                      </p>
                      {group.contribution !== null && (
                        <p className="text-xs text-gray-500">
                          +{group.contribution.toFixed(1)} pts
                        </p>
                      )}
                    </div>
                  </button>

                  {/* Expanded assignments */}
                  {expandedGroup === groupKey && group.assignments.length > 0 && (
                    <div className="px-3 pb-3 space-y-1 max-h-64 overflow-y-auto">
                      {group.assignments.slice(0, 20).map((assignment) => (
                        <div
                          key={assignment.id}
                          className="flex items-center justify-between py-2 px-2 text-sm bg-surface-base/30 rounded"
                        >
                          <div className="flex-1 min-w-0 mr-2">
                            <p className="truncate text-gray-300">{assignment.name}</p>
                            {assignment.due_at && (
                              <p className="text-xs text-gray-500">
                                {format(new Date(assignment.due_at), 'MMM d')}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            {assignment.status === 'graded' && assignment.grade ? (
                              <span className={getGradeColor(
                                assignment.score !== null && assignment.points_possible
                                  ? (assignment.score / assignment.points_possible) * 100
                                  : null
                              )}>
                                {assignment.grade}
                              </span>
                            ) : (
                              <span className="text-gray-500 text-xs">pending</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {group.assignments.length > 20 && (
                        <p className="text-center text-xs text-gray-500 py-1">
                          +{group.assignments.length - 20} more
                        </p>
                      )}
                    </div>
                  )}
                </div>
                )
              })}

              {/* Legend */}
              <p className="text-xs text-gray-500 text-center mt-2">
                Official Canvas grade weights
              </p>
              <p className="text-xs text-gray-500 text-center mt-1">
                Math focus: Homework, Fact Assessments, Written Assessments
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Assignment Status List Component
function AssignmentStatusList({ assignments }: { assignments: Assignment[] }) {
  if (assignments.length === 0) {
    return null
  }

  const getStatusBadge = (assignment: Assignment) => {
    if (assignment.excused) {
      return <span className="px-2 py-0.5 text-xs rounded bg-gray-500/30 text-gray-300">EXCUSED</span>
    }
    if (assignment.missing) {
      return (
        <span className="px-2 py-0.5 text-xs rounded bg-red-500/30 text-red-400">MISSING</span>
      )
    }
    if (assignment.late) {
      return (
        <span className="px-2 py-0.5 text-xs rounded bg-orange-500/30 text-orange-400">LATE</span>
      )
    }
    if (assignment.status === 'graded' && assignment.score !== null) {
      const pct = assignment.points_possible
        ? (assignment.score / assignment.points_possible) * 100
        : 0
      const colorClass =
        pct >= 90
          ? 'text-green-400'
          : pct >= 80
            ? 'text-blue-400'
            : pct >= 70
              ? 'text-yellow-400'
              : 'text-red-400'
      return (
        <span className={`font-bold ${colorClass}`}>
          {assignment.grade || `${Math.round(pct)}%`}
        </span>
      )
    }
    if (assignment.status === 'submitted') {
      return (
        <span className="px-2 py-0.5 text-xs rounded bg-blue-500/30 text-blue-400">SUBMITTED</span>
      )
    }
    return (
      <span className="px-2 py-0.5 text-xs rounded bg-gray-500/30 text-gray-400">PENDING</span>
    )
  }

  return (
    <div className="card">
      <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-quest-primary" />
        Recent Assignments
      </h2>
      <div className="space-y-2">
        {assignments.map((assignment) => (
          <div
            key={assignment.id}
            className={`flex items-center justify-between p-3 rounded-lg ${
              assignment.missing
                ? 'bg-red-900/20 border border-red-500/30'
                : 'bg-surface-card/50'
            }`}
          >
            <div className="flex-1 min-w-0">
              <p
                className={`font-medium truncate ${assignment.missing ? 'text-red-300' : ''}`}
              >
                {assignment.name}
              </p>
              {assignment.due_at && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  {format(new Date(assignment.due_at), 'MMM d')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">{getStatusBadge(assignment)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Check if course is language arts / reading / ELA
function isLanguageArtsCourse(courseName: string): boolean {
  const lower = courseName.toLowerCase()
  return (
    lower.includes('reading') ||
    lower.includes('language arts') ||
    lower.includes('ela') ||
    lower.includes('spelling') ||
    lower.includes('english')
  )
}

// Main CourseDetail Page
export default function ParentCourseDetail() {
  const { studentId, courseId } = useParams()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery<CourseDetailData>({
    queryKey: ['parent-course-detail', courseId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/courses/${courseId}`)
      return response.data
    },
    enabled: !!courseId,
  })

  const markReadMutation = useMutation({
    mutationFn: async (announcementId: number) => {
      await api.patch(`/api/parent/announcements/${announcementId}/read`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-course-detail', courseId] })
    },
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="h-32 bg-surface-card rounded-xl"></div>
        <div className="h-64 bg-surface-card rounded-xl"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p className="text-gray-400">Failed to load course details</p>
        <Link to={`/parent/student/${studentId}`} className="btn btn-primary mt-4">
          Back to Student
        </Link>
      </div>
    )
  }

  const { course, student, modules, announcements, files, pages, assignments } = data

  // Get the short course name (before " - ")
  const courseName = course.name.split(' - ')[0]

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/parent/courses/student/${studentId}`}
          className="p-2 hover:bg-surface-card rounded-lg"
        >
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{courseName}</h1>
          <p className="text-gray-400">{student.name}</p>
        </div>
        {course.current_score !== null && (
          <div className="text-right">
            <div
              className={`text-2xl font-bold ${
                course.current_score >= 90
                  ? 'text-green-400'
                  : course.current_score >= 80
                    ? 'text-blue-400'
                    : course.current_score >= 70
                      ? 'text-yellow-400'
                      : 'text-red-400'
              }`}
            >
              {course.current_grade || `${course.current_score.toFixed(1)}%`}
            </div>
            {course.current_grade && (
              <p className="text-sm text-gray-400">{course.current_score?.toFixed(1)}%</p>
            )}
          </div>
        )}
      </div>

      {/* Missing Alert */}
      {course.missing_count > 0 && (
        <div className="bg-red-900/30 border border-red-500 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <p className="font-bold text-red-300">
              {course.missing_count} Missing Assignment
              {course.missing_count > 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Grade Updated Info */}
      {course.grade_updated_at && (
        <p className="text-center text-xs text-gray-500">
          Grade updated{' '}
          {formatDistanceToNow(new Date(course.grade_updated_at), { addSuffix: true })}
        </p>
      )}

      {/* Grade Weights Breakdown */}
      <GradeWeightsCard courseId={Number(courseId)} />

      {/* Grade History Chart */}
      <CourseGradeChart
        courseId={Number(courseId)}
        courseName={courseName}
        currentScore={course.current_score}
      />

      {/* Spelling Progress - shown for Reading/Language Arts courses */}
      {isLanguageArtsCourse(course.name) && (
        <SpellingProgress studentId={Number(studentId)} studentName={student.name} />
      )}

      {/* Module Progress */}
      <ModuleProgressCard modules={modules} />

      {/* Announcements */}
      <AnnouncementsCard
        announcements={announcements}
        onMarkRead={(id) => markReadMutation.mutate(id)}
      />

      {/* Course Files */}
      <CourseFilesGrid files={files} />

      {/* Wiki Pages */}
      <WikiPagesCard pages={pages} courseId={Number(courseId)} />

      {/* Syllabus */}
      <SyllabusCard syllabus={course.syllabus_body} />

      {/* Recent Assignments */}
      <AssignmentStatusList assignments={assignments} />

      {/* Back to Student */}
      <div className="text-center">
        <Link
          to={`/parent/courses/student/${studentId}`}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {student.name}
        </Link>
      </div>
    </div>
  )
}
